import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  useAnalytics,
  useAnalyticsTrends,
  useProjects,
  useUsers,
  useTimeEntries,
  useActionAlerts,
  useSystemSettings,
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import { 
  Users, 
  Clock, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  CheckSquare,
  XCircle, 
  Calendar,
  BarChart3,
  Target,
  Activity,
  UserCheck,
  UserX,
  CalendarDays,
  Zap,
  Gauge,
  TrendingDown,
  Download,
  FileText,
  FileSpreadsheet,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  X,
  ChevronDown,
  CalendarRange
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfDay, endOfDay, differenceInDays, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import { toUTCPlus5DateOnly } from '../utils/time';

// Reusable Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-secondary-800 border-t border-secondary-200 dark:border-secondary-700 sm:px-6">
      <div className="flex items-center text-sm text-secondary-700 dark:text-secondary-300">
        <span>
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-secondary-500 dark:text-secondary-400 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-l-md hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        {/* Page numbers */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`relative inline-flex items-center px-3 py-2 text-sm font-medium ${
                currentPage === pageNum
                  ? 'z-10 bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400 border-secondary-300 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              } border`}
            >
              {pageNum}
            </button>
          );
        })}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-secondary-500 dark:text-secondary-400 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-r-md hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Helper function to paginate arrays
const paginateArray = (array, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return array.slice(startIndex, endIndex);
};

function formatDuration(hours) {
  // hours is a float (e.g., 0.25 for 15 minutes)
  const totalSeconds = Math.round(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'm ' : ''}${s}s`;
}

function getPeriodDisplayName(period, customRange = null) {
  if (period === 'custom' && customRange) {
    return `${format(new Date(customRange.startDate), 'MMM d')} - ${format(new Date(customRange.endDate), 'MMM d, yyyy')}`;
  }
  
  const periodNames = {
    'today': 'Today',
    'week': 'This Week',
    'last_week': 'Last Week', 
    'month': 'This Month',
    'last_month': 'Last Month',
    'quarter': 'This Quarter',
    'last_30_days': 'Last 30 Days',
    'last_90_days': 'Last 90 Days',
    'custom': 'Custom Range'
  };
  return periodNames[period] || 'This Week';
}

// Enhanced Period Selector Component
const PeriodSelector = ({ selectedPeriod, onPeriodChange, customRange, onCustomRangeChange, loading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempRange, setTempRange] = useState(customRange || { startDate: '', endDate: '' });
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowDatePicker(false);
      }
    };

    if (isOpen || showDatePicker) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, showDatePicker]);

  const periodGroups = [
    {
      title: 'Quick Select',
      periods: [
        { value: 'today', label: 'Today', icon: Calendar },
        { value: 'week', label: 'This Week', icon: CalendarDays },
        { value: 'month', label: 'This Month', icon: Calendar }
      ]
    },
    {
      title: 'Compare',
      periods: [
        { value: 'last_week', label: 'Last Week', icon: CalendarDays },
        { value: 'last_month', label: 'Last Month', icon: Calendar },
        { value: 'quarter', label: 'This Quarter', icon: BarChart3 }
      ]
    },
    {
      title: 'Rolling Periods',
      periods: [
        { value: 'last_30_days', label: 'Last 30 Days', icon: Clock },
        { value: 'last_90_days', label: 'Last 90 Days', icon: TrendingUp }
      ]
    }
  ];

  const handlePeriodSelect = (period) => {
    onPeriodChange(period);
    setIsOpen(false);
    if (period !== 'custom') {
      setShowDatePicker(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (tempRange.startDate && tempRange.endDate) {
      onCustomRangeChange(tempRange);
      onPeriodChange('custom');
      setShowDatePicker(false);
      setIsOpen(false);
    }
  };

  const handleCustomRangeShow = () => {
    setShowDatePicker(true);
    setTempRange(customRange || { 
      startDate: format(new Date(), 'yyyy-MM-dd'), 
      endDate: format(new Date(), 'yyyy-MM-dd') 
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`flex items-center gap-2 px-4 py-2 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Calendar className="h-4 w-4 text-primary-600" />
        <span className="font-medium text-sm">
          {getPeriodDisplayName(selectedPeriod, customRange)}
        </span>
        <ChevronDown className={`h-4 w-4 text-primary-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {loading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-xl shadow-xl border border-secondary-200 dark:border-secondary-700 z-50 backdrop-blur-sm">
          <div className="p-3">
            {periodGroups.map((group, groupIndex) => (
              <div key={group.title} className={groupIndex > 0 ? 'border-t border-secondary-100 pt-2 mt-2' : ''}>
                <div className="px-2 py-1 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  {group.title}
                </div>
                <div className="space-y-1">
                  {group.periods.map((period) => {
                    const IconComponent = period.icon;
                    return (
                      <button
                        key={period.value}
                        onClick={() => handlePeriodSelect(period.value)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                          selectedPeriod === period.value 
                            ? 'bg-primary-100 text-primary-700 font-medium shadow-sm' 
                            : 'text-secondary-700 hover:bg-secondary-50 hover:scale-[1.02] hover:shadow-sm'
                        }`}
                      >
                        <IconComponent className="h-4 w-4" />
                        {period.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Custom Range Section */}
            <div className="border-t border-secondary-100 pt-3 mt-3">
              <div className="px-2 py-1 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Custom
              </div>
              <button
                onClick={handleCustomRangeShow}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                  selectedPeriod === 'custom' 
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 font-medium shadow-sm' 
                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:scale-[1.02] hover:shadow-sm'
                }`}
              >
                <CalendarRange className="h-4 w-4" />
                Custom Range
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">Select Date Range</h3>
              <button
                onClick={() => setShowDatePicker(false)}
                className="p-2 hover:bg-secondary-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-secondary-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempRange.startDate}
                  onChange={(e) => setTempRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={tempRange.endDate}
                  onChange={(e) => setTempRange(prev => ({ ...prev, endDate: e.target.value }))}
                  min={tempRange.startDate}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDatePicker(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 rounded-lg hover:bg-secondary-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomRangeApply}
                disabled={!tempRange.startDate || !tempRange.endDate}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Breakdown Modal Component
const TaskBreakdownModal = ({ isOpen, onClose, taskCategories, totalTasks }) => {

  if (!isOpen) return null;



  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-2xl font-bold text-secondary-900">Tasks Breakdown</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-secondary-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Active Tasks - On Time */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('active-ontime')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-blue-900">Active & On-Time</h4>
                <div className="p-2 bg-blue-100 rounded-full">
                  <Activity className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-blue-900">{taskCategories.activeOnTimeTasks}</p>
              <p className="text-sm text-blue-700">Tasks in progress with no overdue</p>
              <div className="mt-2 flex items-center text-xs text-blue-600">
                <span className="hover:text-blue-700 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>

            {/* Active Tasks - Overdue */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('active-overdue')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-50 border border-orange-200 rounded-lg p-4 cursor-pointer hover:bg-orange-100 hover:border-orange-300 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-orange-900">Active & Overdue</h4>
                <div className="p-2 bg-orange-100 rounded-full">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-900">{taskCategories.activeOverdueTasks}</p>
              <p className="text-sm text-orange-700">Tasks in progress but overdue</p>
              <div className="mt-2 flex items-center text-xs text-orange-600">
                <span className="hover:text-orange-700 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>

            {/* Completed Tasks - On Time */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('completed-ontime')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:bg-green-100 hover:border-green-300 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-green-900">Completed On-Time</h4>
                <div className="p-2 bg-green-100 rounded-full">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-green-900">{taskCategories.completedOnTimeTasks}</p>
              <p className="text-sm text-green-700">Tasks completed before due date</p>
              <div className="mt-2 flex items-center text-xs text-green-600">
                <span className="hover:text-green-700 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>

            {/* Completed Tasks - Late */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('completed-late')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40 rounded-lg p-4 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:border-rose-300 dark:hover:border-rose-700 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-rose-900 dark:text-rose-200">Completed Late</h4>
                <div className="p-2 bg-rose-100 dark:bg-rose-900/40 rounded-full">
                  <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-rose-900 dark:text-rose-200">{taskCategories.completedLateTasks}</p>
              <p className="text-sm text-rose-700 dark:text-rose-300">Tasks completed after due date</p>
              <div className="mt-2 flex items-center text-xs text-rose-600 dark:text-rose-400">
                <span className="hover:text-rose-700 dark:hover:text-rose-300 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>

            {/* Pending Tasks - On Time */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('pending-ontime')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-50 dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-700 hover:border-gray-300 dark:hover:border-secondary-600 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">Pending & On-Time</h4>
                <div className="p-2 bg-gray-100 dark:bg-secondary-700 rounded-full">
                  <Calendar className="h-4 w-4 text-gray-600 dark:text-secondary-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{taskCategories.pendingOnTimeTasks}</p>
              <p className="text-sm text-gray-700 dark:text-secondary-300">Tasks not started, not overdue</p>
              <div className="mt-2 flex items-center text-xs text-gray-600 dark:text-secondary-400">
                <span className="hover:text-gray-700 dark:hover:text-secondary-300 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>

            {/* Pending Tasks - Overdue */}
            <a 
              href={`/tasks?filter=${encodeURIComponent('pending-overdue')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40 rounded-lg p-4 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:border-rose-300 dark:hover:border-rose-700 transition-all duration-200 hover:scale-105 block"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-rose-900 dark:text-rose-200">Pending & Overdue</h4>
                <div className="p-2 bg-rose-100 dark:bg-rose-900/40 rounded-full">
                  <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-rose-900 dark:text-rose-200">{taskCategories.pendingOverdueTasks}</p>
              <p className="text-sm text-rose-700 dark:text-rose-300">Tasks not started, overdue</p>
              <div className="mt-2 flex items-center text-xs text-rose-600 dark:text-rose-400">
                <span className="hover:text-rose-700 dark:hover:text-rose-300 transition-colors">
                  Click to view
                  <ExternalLink className="h-3 w-3 ml-1" />
                </span>
              </div>
            </a>
          </div>          
        </div>
      </div>
    </div>
  );
};

// Chart.js color config for dark mode - ensures axis labels, grid, legend are visible on dark cards
const getChartColors = (isDark) => {
  if (!isDark) return {};
  const textColor = '#94a3b8';
  const gridColor = 'rgba(148, 163, 184, 0.25)';
  const titleColor = '#e2e8f0';
  return {
    scales: {
      x: { ticks: { color: textColor }, grid: { color: gridColor }, title: { color: titleColor } },
      y: { ticks: { color: textColor }, grid: { color: gridColor }, title: { color: titleColor } },
    },
    plugins: {
      legend: { labels: { color: titleColor } },
    },
  };
};

const getMobileChartDefaults = () => {
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;
  if (!isNarrow) return {};
  return {
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          maxTicksLimit: 6,
          autoSkip: true,
          font: { size: 10 },
        },
      },
      y: {
        ticks: {
          maxTicksLimit: 6,
          font: { size: 10 },
        },
      },
    },
    plugins: {
      legend: {
        labels: { boxWidth: 10, font: { size: 11 }, padding: 8 },
      },
    },
  };
};

const deepMergeChart = (target = {}, source = {}) => {
  const out = { ...target };
  Object.keys(source).forEach((key) => {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object'
    ) {
      out[key] = deepMergeChart(target[key], source[key]);
    } else {
      out[key] = source[key];
    }
  });
  return out;
};

const mergeChartOptions = (base, colors) => {
  const mobile = getMobileChartDefaults();
  let merged = deepMergeChart(base, mobile);
  if (!colors.scales && !colors.plugins) return merged;
  return deepMergeChart(merged, {
    scales: colors.scales || {},
    plugins: colors.plugins || {},
  });
};

const Analytics = () => {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = resolvedTheme === 'dark';
  const chartColors = getChartColors(isDark);
  const { data: systemSettings } = useSystemSettings();
  const dailyTargetHours = systemSettings?.daily_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.daily_target_hours;
  const weeklyTargetHours = systemSettings?.weekly_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.weekly_target_hours;
  
  // State declarations - must be before hook calls that use them
  const [selectedPeriod, setSelectedPeriod] = useState('week'); // week, month, quarter, custom
  const [customRange, setCustomRange] = useState(null);
  
//
  const { 
    data: analyticsData, 
    isLoading: analyticsLoading 
  } = useAnalytics(selectedPeriod, customRange);

  const [comparePrevious, setComparePrevious] = useState(false);

  const { 
    data: trendsData, 
    isLoading: trendsLoading 
  } = useAnalyticsTrends(selectedPeriod, customRange);

  // Previous period for comparison: week->last_week, month->last_month, last_30_days->custom 30 days before
  const getPreviousPeriod = useCallback(() => {
    if (selectedPeriod === 'week') return { period: 'last_week', customRange: null };
    if (selectedPeriod === 'month') return { period: 'last_month', customRange: null };
    if (selectedPeriod === 'last_week') {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 14);
      return { period: 'custom', customRange: { startDate: format(start, 'yyyy-MM-dd'), endDate: format(new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd') } };
    }
    if (selectedPeriod === 'last_30_days' && !customRange) {
      const end = new Date();
      end.setDate(end.getDate() - 30);
      const start = new Date(end);
      start.setDate(start.getDate() - 30);
      return { period: 'custom', customRange: { startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') } };
    }
    if (selectedPeriod === 'custom' && customRange) {
      const start = new Date(customRange.startDate);
      const end = new Date(customRange.endDate);
      const days = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
      const prevEnd = new Date(start);
      prevEnd.setDate(prevEnd.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevStart.getDate() - days + 1);
      return { period: 'custom', customRange: { startDate: format(prevStart, 'yyyy-MM-dd'), endDate: format(prevEnd, 'yyyy-MM-dd') } };
    }
    return null;
  }, [selectedPeriod, customRange]);

  const previousPeriodConfig = getPreviousPeriod();
  const { data: previousTrendsData } = useAnalyticsTrends(
    previousPeriodConfig?.period ?? null,
    previousPeriodConfig?.customRange ?? undefined,
    !!comparePrevious && !!previousPeriodConfig
  );
  const canComparePrevious = !!previousPeriodConfig && ['week', 'last_week', 'month', 'last_month', 'last_30_days', 'custom'].includes(selectedPeriod);
  
  const { 
    data: projectsData, 
    isLoading: projectsLoading 
  } = useProjects();

  const { 
    data: usersData, 
    isLoading: usersLoading 
  } = useUsers();

  const { 
    data: timeEntriesData, 
    isLoading: timeEntriesLoading 
  } = useTimeEntries();

  const ACTION_ALERTS_PREF_KEY = 'ticksync.analytics.actionAlertsVisible';
  const [showActionAlerts, setShowActionAlerts] = useState(() => {
    try {
      const stored = localStorage.getItem(ACTION_ALERTS_PREF_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const setActionAlertsVisible = useCallback((visible) => {
    setShowActionAlerts(visible);
    try {
      localStorage.setItem(ACTION_ALERTS_PREF_KEY, String(visible));
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const {
    data: actionAlertsData,
    isLoading: actionAlertsLoading,
  } = useActionAlerts(4, user?.role === 'admin' && showActionAlerts);
  
  // Extract data from React Query responses
  const analytics = analyticsData || null;
  const projects = projectsData?.results || [];
  const users = Array.isArray(usersData) ? usersData : (usersData?.results || []);
  const timeEntries = timeEntriesData?.results || [];
  
  // Combined loading state from React Query
  const isLoading = analyticsLoading || projectsLoading || usersLoading || timeEntriesLoading;
  const [selectedView, setSelectedView] = useState('overview'); // overview, users, tasks, projects, efficiency
  const [showTaskBreakdownModal, setShowTaskBreakdownModal] = useState(false);
  const [userFilter, setUserFilter] = useState('all'); // all, overloaded, underutilized, normal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [alertFilter, setAlertFilter] = useState('this_week'); // this_week | chronic

  const filteredActionAlerts = useMemo(() => {
    const alerts = actionAlertsData?.alerts || [];
    if (alertFilter === 'chronic') {
      return alerts.filter((a) => a.chronic);
    }
    return alerts.filter((a) => a.under_this_week);
  }, [actionAlertsData, alertFilter]);

  const weekDotClass = (status) => {
    if (status === 'on_track') return 'bg-emerald-500';
    if (status === 'under_target') return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Helper function to get target hours based on selected period
  const getTargetHours = useCallback(() => {
    // Prefer backend-computed target when available (includes mid-week proration)
    if (analytics?.targetHours != null && Number.isFinite(analytics.targetHours)) {
      return analytics.targetHours;
    }

    if (selectedPeriod === 'custom' && customRange) {
      // Calculate target hours based on custom range duration
      const startDate = new Date(customRange.startDate);
      const endDate = new Date(customRange.endDate);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      const weekdays = Math.floor(diffDays * 5 / 7); // Approximate weekdays
      return weekdays * dailyTargetHours;
    }

    // Prorate current work week by elapsed weekdays (Mon–Fri)
    if (selectedPeriod === 'week') {
      const now = new Date();
      const day = now.getDay(); // 0=Sun ... 6=Sat
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setHours(0, 0, 0, 0);
      monday.setDate(now.getDate() + mondayOffset);
      let elapsed = 0;
      for (let d = new Date(monday); d <= now; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay();
        if (wd !== 0 && wd !== 6) elapsed += 1;
      }
      return Math.max(elapsed, 1) * dailyTargetHours;
    }
    
    switch (selectedPeriod) {
      case 'today':
        return dailyTargetHours;
      case 'last_week':
        return weeklyTargetHours;
      case 'month':
      case 'last_month':
        return weeklyTargetHours * 4;
      case 'quarter':
        return weeklyTargetHours * 12;
      case 'last_30_days':
        return weeklyTargetHours * 4;
      case 'last_90_days':
        return weeklyTargetHours * 12;
      default:
        return weeklyTargetHours;
    }
  }, [selectedPeriod, customRange, analytics?.targetHours, dailyTargetHours, weeklyTargetHours]);

  // Helper function to get target period label
  const getTargetPeriodLabel = useCallback(() => {
    switch (selectedPeriod) {
      case 'today':
        return 'day';
      case 'week':
        return 'week';
      case 'last_week':
        return 'week';
      case 'month':
        return 'month';
      case 'last_month':
        return 'month';
      case 'quarter':
        return 'quarter';
      case 'last_30_days':
        return '30 days';
      case 'last_90_days':
        return '90 days';
      default:
        return 'week';
    }
  }, [selectedPeriod]);

  // Pagination states for different lists
  const [pagination, setPagination] = useState({
    topPerformers: { currentPage: 1, itemsPerPage: 5 },
    performanceIssues: { currentPage: 1, itemsPerPage: 10 },
    userDetails: { currentPage: 1, itemsPerPage: 10 },
    taskEfficiency: { currentPage: 1, itemsPerPage: 10 },
    highIdleEntries: { currentPage: 1, itemsPerPage: 10 },
    overdueTasks: { currentPage: 1, itemsPerPage: 10 },
    dueTodayTasks: { currentPage: 1, itemsPerPage: 10 },
    highPriorityTasks: { currentPage: 1, itemsPerPage: 10 },
    projectAnalytics: { currentPage: 1, itemsPerPage: 10 }
  });

  // Function to update pagination for a specific list
  const updatePagination = (listName, newPage) => {
    setPagination(prev => ({
      ...prev,
      [listName]: { ...prev[listName], currentPage: newPage }
    }));
  };

  // Function to reset pagination when view changes
  const resetPagination = () => {
    setPagination({
      topPerformers: { currentPage: 1, itemsPerPage: 5 },
      performanceIssues: { currentPage: 1, itemsPerPage: 10 },
      userDetails: { currentPage: 1, itemsPerPage: 10 },
      taskEfficiency: { currentPage: 1, itemsPerPage: 10 },
      highIdleEntries: { currentPage: 1, itemsPerPage: 10 },
      overdueTasks: { currentPage: 1, itemsPerPage: 10 },
      dueTodayTasks: { currentPage: 1, itemsPerPage: 10 },
      highPriorityTasks: { currentPage: 1, itemsPerPage: 10 },
      projectAnalytics: { currentPage: 1, itemsPerPage: 10 }
    });
  };

  // 🎯 NO MORE useEffect FOR DATA FETCHING! React Query handles it automatically
  
  // Only keep useEffect for UI interactions

  useEffect(() => {
    resetPagination();
  }, [selectedView]);

  // 🎯 NO MORE FETCH FUNCTIONS! React Query handles everything automatically


  const handleExportCSV = async (reportType = 'summary') => {
    try {
      const response = await axios.get(`/api/analytics/export/csv?period=${selectedPeriod}&report_type=${reportType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${reportType}_${selectedPeriod}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${reportType} report exported as CSV`);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`/api/analytics/export/excel?period=${selectedPeriod}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${selectedPeriod}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Complete analytics report exported as Excel');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleUserViewClick = (filterType) => {
    setSelectedView('users');
    setUserFilter(filterType);
    resetPagination();
  };

  const handleUserClick = async (user) => {
    try {
      setLoadingUserDetails(true);
      const response = await axios.get(`/api/analytics/user-details/${user.id}?period=${selectedPeriod}`);
      setSelectedUser(response.data);
      setShowUserDetailsModal(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const getFilteredUsers = () => {
    if (!analytics?.userDetails) return [];
    
    if (userFilter === 'all') return analytics?.userDetails || [];
    
    return analytics?.userDetails?.filter(user => {
      // Prefer server-computed status (leave-aware); fall back to hours vs target
      if (user.utilizationStatus) {
        if (userFilter === 'overloaded') return user.utilizationStatus === 'overloaded';
        if (userFilter === 'underutilized') return user.utilizationStatus === 'underutilized';
        if (userFilter === 'normal') return user.utilizationStatus === 'normal';
      }
      const targetHours = user.targetHours ?? getTargetHours();
      if (userFilter === 'overloaded') {
        return user.weeklyHours > targetHours;
      } else if (userFilter === 'underutilized') {
        return user.weeklyHours < (targetHours * 0.75);
      } else if (userFilter === 'normal') {
        return user.weeklyHours >= (targetHours * 0.75) && user.weeklyHours <= targetHours;
      }
      return true;
    });
  };

  const handleExportUsersCSV = () => {
    const filteredUsers = getFilteredUsers();
    const csvData = [
      ['Name', 'Email', 'Daily Hours', 
       `${getPeriodDisplayName(selectedPeriod)} Hours`, 
       'Idle Hours', 'Idle %', 'Performance', 'Efficiency', 'Active Tasks', 'Completed Tasks', 'Status'],
      ...filteredUsers.map(user => [
        user.name,
        user.email,
        user.dailyHours.toFixed(1),
        user.weeklyHours.toFixed(1),
        user.idleHours?.toFixed(1) || '0',
        user.idlePercentage?.toFixed(1) || '0',
        user.performance,
        user.efficiency != null ? user.efficiency.toFixed(1) : 'N/A',
        user.activeTasks,
        user.completedTasks || 0,
        user.utilizationStatus || 'normal'
      ])
    ];
    
    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `users_${userFilter}_${selectedPeriod}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success(`${userFilter} users exported as CSV`);
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceIcon = (percentage) => {
    if (percentage >= 90) return <UserCheck className="h-4 w-4" />;
    if (percentage >= 75) return <Activity className="h-4 w-4" />;
    return <UserX className="h-4 w-4" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'text-red-600';
      case 'due-today': return 'text-orange-600';
      case 'due-soon': return 'text-yellow-600';
      case 'on-track': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency == null) return 'text-gray-600';
    if (efficiency >= 100) return 'text-green-600';
    if (efficiency >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEfficiencyIcon = (efficiency) => {
    if (efficiency == null) return <Activity className="h-4 w-4" />;
    if (efficiency >= 100) return <TrendingUp className="h-4 w-4" />;
    if (efficiency >= 80) return <Target className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  // Show error state only if analytics failed to load and we're not loading
  if (!analytics && !isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-600">Failed to load analytics data</p>
      </div>
    );
  }

  // Loading component for individual sections
  const SectionLoader = ({ height = "h-32" }) => (
    <div className={`bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6 flex items-center justify-center ${height}`}>
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
        <span className="text-sm text-secondary-600">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      
      
              {/* Welcome Message for Admin */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <Avatar 
            blobName={user?.avatar}
            alt={user?.full_name || user?.username || 'User'}
            size="lg"
            className="w-12 h-12"
            fallbackText={user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
          />
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-1">Welcome, {user?.full_name?.split(' ')[0] || 'Admin'}! 🚀</h1>
            <p className="text-secondary-600 text-sm">Here's your team's member analytics dashboard. Track member performance and productivity!</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 mt-2 md:mt-0">
          <div className="flex items-center gap-3">
            <PeriodSelector
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
              loading={isLoading}
            />
            <div className="flex items-center">
              <select
                className="input-field w-32 border-primary-300 focus:ring-primary-500"
                value={selectedView}
                onChange={(e) => setSelectedView(e.target.value)}
              >
                <option value="overview">Overview</option>
                <option value="users">Users</option>
                <option value="tasks">Tasks</option>
                <option value="projects">Projects</option>
                <option value="efficiency">Efficiency</option>
                <option value="idle">Idle Detection</option>
              </select>
            </div>
          </div>
          
          {/* Export Buttons */}
          <div className="flex items-center gap-2 border-l border-secondary-300 pl-4">
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              title="Export Complete Report as Excel"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>
            
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                <FileText className="h-4 w-4" />
                CSV
                <Download className="h-3 w-3" />
              </button>
              
              {/* CSV Export Dropdown */}
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-2">
                  <button
                    onClick={() => handleExportCSV('summary')}
                    className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center gap-2"
                  >
                    <BarChart3 className="h-4 w-4" />
                    Summary Report
                  </button>
                  <button
                    onClick={() => handleExportCSV('users')}
                    className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center gap-2"
                  >
                    <Users className="h-4 w-4" />
                    Users Report
                  </button>
                  <button
                    onClick={() => handleExportCSV('projects')}
                    className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Projects Report
                  </button>
                  <button
                    onClick={() => handleExportCSV('detailed')}
                    className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center gap-2"
                  >
                    <Target className="h-4 w-4" />
                    Efficiency Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Dashboard */}
      {selectedView === 'overview' && (
        <>
          {/* Period Loading Overlay - Removed to allow progressive loading */}
          
          {/* Key Metrics Cards - Simplified stats section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/users')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Total Users</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm text-secondary-500">Loading...</span>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-secondary-900">{analytics?.totalUsers || 0}</p>
                  )}
                </div>
                <div className="p-2 bg-blue-100 rounded-full">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/time-entries')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Total Hours Tracked</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm text-secondary-500">Loading...</span>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-secondary-900">{analytics?.totalHours?.toFixed(1) || '0.0'}h</p>
                  )}
                </div>
                <div className="p-2 bg-green-100 rounded-full">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/projects')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Total Projects</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm text-secondary-500">Loading...</span>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-secondary-900">{analytics?.totalProjects || 0}</p>
                  )}
                </div>
                <div className="p-2 bg-indigo-100 rounded-full">
                  <FolderOpen className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow hover:scale-105 transition-transform" onClick={() => setShowTaskBreakdownModal(true)}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Total Tasks</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm text-secondary-500">Loading...</span>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-secondary-900">{analytics?.totalTasks || 0}</p>
                  )}
                </div>
                <div className="p-2 bg-teal-100 rounded-full">
                  <CheckSquare className="h-5 w-5 text-teal-600" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow group" onClick={() => setSelectedView('idle')}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-secondary-600 mb-1">Idle Detection</p>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm text-secondary-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-xl font-bold text-purple-600 group-hover:text-purple-700 transition-colors">
                        {analytics?.totalIdleHours?.toFixed(1) || 0}h
                      </p>
                      <p className="text-xs text-secondary-600 mb-1">{analytics?.totalIdlePercentage?.toFixed(1) || 0}% of tracked time</p>
                    </>
                  )}
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-secondary-200 rounded-full h-1 overflow-hidden mb-1">
                    <div 
                      className="bg-purple-600 dark:bg-purple-500 h-full rounded-full transition-all duration-500 ease-out" 
                      style={{ width: `${Math.min(analytics?.totalIdlePercentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  
                  {/* Status Message */}
                  <div>
                    {analytics?.totalIdlePercentage > 0 ? (
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-orange-500" />
                        <span className="text-xs text-orange-600 font-medium">Idle Time Detected</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-green-500 font-medium">100% Productive</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-full ml-3">
                  <Gauge className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>
          </div>



          {/* Utilization Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleUserViewClick('overloaded')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Overloaded Users</p>
                  <p className="text-xl font-bold text-orange-600">{analytics?.overloadedUsers || 0}</p>
                  <p className="text-xs text-secondary-600">Working &gt;{getTargetHours()}h target</p>
                </div>
                <div className="p-2 bg-orange-100 rounded-full">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleUserViewClick('underutilized')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-secondary-600 mb-1">Underutilized Users</p>
                  <p className="text-xl font-bold text-blue-600">{analytics?.underutilizedUsers || 0}</p>
                  <p className="text-xs text-secondary-600">Working &lt;{(getTargetHours() * 0.75).toFixed(0)}h target</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-full">
                  <TrendingDown className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Action Alerts — 40h weekly compliance for management triage */}
          {user?.role === 'admin' && !showActionAlerts && (
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-dashed border-secondary-300 dark:border-secondary-600 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="h-4 w-4 text-secondary-400 shrink-0" />
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Action Alerts are hidden
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActionAlertsVisible(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors"
                aria-label="Show Action Alerts"
                title="Show Action Alerts"
              >
                Show Action Alerts
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {user?.role === 'admin' && showActionAlerts && (
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 shrink-0">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-secondary-900 dark:text-white">
                      Action Alerts
                    </h2>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400">
                      Under 40h this week or chronic under-hours — review before timesheets
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="inline-flex rounded-lg border border-secondary-200 dark:border-secondary-600 p-0.5 bg-secondary-50 dark:bg-secondary-900/40">
                    <button
                      type="button"
                      onClick={() => setAlertFilter('this_week')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        alertFilter === 'this_week'
                          ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white shadow-sm'
                          : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900'
                      }`}
                    >
                      This Week
                      {!actionAlertsLoading && (
                        <span className="ml-1.5 text-secondary-400">
                          ({actionAlertsData?.counts?.this_week ?? 0})
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAlertFilter('chronic')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        alertFilter === 'chronic'
                          ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white shadow-sm'
                          : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900'
                      }`}
                    >
                      Chronic (2+)
                      {!actionAlertsLoading && (
                        <span className="ml-1.5 text-secondary-400">
                          ({actionAlertsData?.counts?.chronic ?? 0})
                        </span>
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActionAlertsVisible(false)}
                    className="p-1.5 rounded-lg text-secondary-500 hover:text-secondary-800 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-700 dark:hover:text-secondary-200 transition-colors"
                    aria-label="Collapse Action Alerts"
                    title="Collapse"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {actionAlertsLoading ? (
                <div className="flex items-center gap-2 px-4 py-8 text-sm text-secondary-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                  Loading action alerts…
                </div>
              ) : filteredActionAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    {alertFilter === 'chronic'
                      ? 'No chronic under-hours patterns'
                      : 'All team members are on track this week'}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Everyone meeting the 40h weekly target for this filter
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-secondary-100 dark:divide-secondary-700">
                  {filteredActionAlerts.map((alert) => (
                    <div
                      key={alert.user_id}
                      className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3 hover:bg-secondary-50/70 dark:hover:bg-secondary-900/30 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 lg:w-56 shrink-0">
                        <Avatar fallbackText={alert.user_name} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                            {alert.user_name}
                          </p>
                          <p className="text-xs text-secondary-500 truncate">
                            {alert.role === 'project_lead' ? 'Project Lead' : 'Member'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 flex-1 min-w-0 flex-wrap">
                        <div className="shrink-0">
                          <p className="text-[11px] uppercase tracking-wide text-secondary-500">This week</p>
                          <p
                            className={`text-sm font-semibold tabular-nums ${
                              alert.this_week_status === 'on_track'
                                ? 'text-emerald-600'
                                : alert.this_week_status === 'under_target'
                                  ? 'text-amber-600'
                                  : 'text-red-600'
                            }`}
                          >
                            {(alert.this_week_hours ?? 0).toFixed(1)}h
                            <span className="text-secondary-400 font-normal">
                              {' '}
                              / {(alert.this_week_target ?? 40).toFixed(0)}h
                            </span>
                          </p>
                        </div>

                        <div className="shrink-0">
                          <p className="text-[11px] uppercase tracking-wide text-secondary-500 mb-1">
                            Last 4 weeks
                          </p>
                          <div className="flex items-center gap-1" title={alert.recommendation}>
                            {(alert.weeks || []).map((w) => (
                              <span
                                key={w.week_start}
                                className={`h-2.5 w-2.5 rounded-full ${weekDotClass(w.status)}`}
                                title={`${w.week_start}: ${w.total_hours}h`}
                              />
                            ))}
                          </div>
                        </div>

                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            alert.severity === 'at_risk'
                              ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                              : 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          }`}
                        >
                          {alert.severity === 'at_risk' ? 'At Risk' : 'Needs Attention'}
                        </span>

                        <p className="text-xs text-secondary-500 dark:text-secondary-400 flex-1 min-w-[12rem] truncate">
                          {alert.recommendation}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(`/users/${alert.user_id}`)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 shrink-0"
                      >
                        View
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Trends & Charts */}
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary-600" />
                Trends &amp; Analytics
              </h2>
              {canComparePrevious && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={comparePrevious}
                    onChange={(e) => setComparePrevious(e.target.checked)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700">Compare to previous period</span>
                </label>
              )}
            </div>

            {/* Hours & Idle over time (+ optional previous period) */}
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Hours &amp; Idle Over Time</h3>
              {trendsLoading ? (
                <SectionLoader height="h-72" />
              ) : trendsData?.hoursByDay?.length > 0 ? (
                <div className="h-56 sm:h-64 md:h-72">
                  <Line
                    data={{
                      labels: (trendsData.hoursByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                      datasets: [
                        {
                          label: 'Total hours',
                          data: (trendsData.hoursByDay || []).map((d) => d.hours),
                          borderColor: '#3B82F6',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          fill: true,
                          tension: 0.3,
                          pointRadius: 4,
                          pointHoverRadius: 6,
                        },
                        ...(trendsData.idleHoursByDay?.length
                          ? [{
                              label: 'Idle hours',
                              data: (trendsData.hoursByDay || []).map((d) => {
                                const idle = trendsData.idleHoursByDay.find((x) => x.date === d.date);
                                return idle ? idle.hours : 0;
                              }),
                              borderColor: '#F59E0B',
                              backgroundColor: 'rgba(245, 158, 11, 0.1)',
                              fill: true,
                              tension: 0.3,
                              pointRadius: 4,
                              pointHoverRadius: 6,
                            }]
                          : []),
                        ...(comparePrevious && previousTrendsData?.hoursByDay?.length
                          ? [{
                              label: 'Previous period (hours)',
                              data: (trendsData.hoursByDay || []).map((d, i) => {
                                const prev = previousTrendsData.hoursByDay[i];
                                return prev ? prev.hours : null;
                              }),
                              borderColor: '#9CA3AF',
                              borderDash: [5, 5],
                              fill: false,
                              pointRadius: 3,
                              pointHoverRadius: 5,
                            }]
                          : []),
                      ].filter(Boolean),
                    }}
                    options={mergeChartOptions({
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true, position: 'top' },
                        tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y != null ? ctx.parsed.y + 'h' : '—'}` } },
                      },
                      scales: {
                        y: { beginAtZero: true, title: { display: true, text: 'Hours' }, ticks: { stepSize: 1 } },
                        x: { title: { display: true, text: 'Date' }, ticks: { maxRotation: 45, minRotation: 35, maxTicksLimit: 14 } },
                      },
                    }, chartColors)}
                  />
                </div>
              ) : (
                <div className="h-72 flex items-center justify-center text-secondary-500">
                  <div className="text-center">
                    <Clock className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                    <p>No time tracked in this period</p>
                  </div>
                </div>
              )}
            </div>

            {/* Utilization over time (stacked) + Active users by day */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Utilization Over Time (weekly)</h3>
                {trendsLoading ? (
                  <SectionLoader height="h-64" />
                ) : trendsData?.utilizationByDay?.length > 0 ? (
                  <div className="h-48 sm:h-56 md:h-64">
                    <Bar
                      data={{
                        labels: (trendsData.utilizationByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                        datasets: [
                          { label: 'Overloaded (≥40h)', data: (trendsData.utilizationByDay || []).map((d) => d.overloaded), backgroundColor: '#F97316', borderRadius: 4 },
                          { label: 'Normal (30–40h)', data: (trendsData.utilizationByDay || []).map((d) => d.normal), backgroundColor: '#10B981', borderRadius: 4 },
                          { label: 'Underutilized (&lt;30h)', data: (trendsData.utilizationByDay || []).map((d) => d.underutilized), backgroundColor: '#3B82F6', borderRadius: 4 },
                        ],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'top' }, tooltip: { mode: 'index', intersect: false } },
                        scales: {
                          x: { stacked: true, ticks: { maxRotation: 45, maxTicksLimit: 12 } },
                          y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Users' } },
                        },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-secondary-500">
                    <div className="text-center">
                      <Users className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                      <p>No utilization data</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Active Users by Day</h3>
                {trendsLoading ? (
                  <SectionLoader height="h-64" />
                ) : trendsData?.activeUsersByDay?.length > 0 ? (
                  <div className="h-48 sm:h-56 md:h-64">
                    <Line
                      data={{
                        labels: (trendsData.activeUsersByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                        datasets: [{
                          label: 'Active users',
                          data: (trendsData.activeUsersByDay || []).map((d) => d.count),
                          borderColor: '#8B5CF6',
                          backgroundColor: 'rgba(139, 92, 246, 0.1)',
                          fill: true,
                          tension: 0.3,
                          pointRadius: 4,
                        }],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} users` } } },
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Users' } },
                          x: { ticks: { maxRotation: 45, maxTicksLimit: 12 } },
                        },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-secondary-500">
                    <div className="text-center">
                      <Activity className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                      <p>No active user data</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top performers bar + Task status doughnut */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Top Performers by Hours</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : (() => {
                  const performers = (analytics?.topPerformers || []).filter((p) => p.hours >= 0.5);
                  return performers.length > 0 ? (
                    <div className="h-48 sm:h-56 md:h-64">
                      <Bar
                        data={{
                          labels: performers.map((p) => p.name.length > 20 ? p.name.slice(0, 18) + '…' : p.name),
                          datasets: [{ label: 'Hours', data: performers.map((p) => p.hours), backgroundColor: '#10B981', borderRadius: 6 }],
                        }}
                        options={mergeChartOptions({
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}h` } } },
                          scales: { x: { beginAtZero: true, title: { display: true, text: 'Hours' }, ticks: { stepSize: 1 } }, y: { ticks: { maxRotation: 0, autoSkip: false } } },
                        }, chartColors)}
                      />
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-secondary-500">
                      <div className="text-center">
                        <TrendingUp className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                        <p>No performers with tracked hours yet</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Task Status Distribution</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : analytics?.taskStatusDistribution?.length > 0 ? (
                  <div className="h-48 sm:h-56 md:h-64">
                    <Doughnut
                      data={{
                        labels: analytics.taskStatusDistribution.map((s) => s.name),
                        datasets: [{
                          data: analytics.taskStatusDistribution.map((s) => s.count),
                          backgroundColor: analytics.taskStatusDistribution.map((s) => s.color || '#6B7280'),
                          borderWidth: 2,
                          borderColor: isDark ? '#334155' : '#fff',
                        }],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16 } },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => {
                                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                const pct = total ? ((ctx.raw / total) * 100).toFixed(1) : 0;
                                return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
                              },
                            },
                          },
                        },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-secondary-500">
                    <div className="text-center">
                      <CheckSquare className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                      <p>No tasks yet</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hours by project + User hours distribution (buckets) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Hours by Project</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : analytics?.projectAnalytics?.length > 0 ? (
                  <div className="h-48 sm:h-56 md:h-64">
                    <Bar
                      data={{
                        labels: (analytics.projectAnalytics || []).slice(0, 10).map((p) => (p.name && p.name.length > 18 ? p.name.slice(0, 16) + '…' : p.name) || 'Unknown'),
                        datasets: [{
                          label: 'Hours',
                          data: (analytics.projectAnalytics || []).slice(0, 10).map((p) => p.totalHours || 0),
                          backgroundColor: '#6366F1',
                          borderRadius: 6,
                        }],
                      }}
                      options={mergeChartOptions({
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}h` } } },
                        scales: { x: { beginAtZero: true, title: { display: true, text: 'Hours' } }, y: { ticks: { maxRotation: 0, autoSkip: false } } },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-secondary-500">
                    <div className="text-center">
                      <FolderOpen className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                      <p>No project data</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">User Hours Distribution</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : (() => {
                  const details = analytics?.userDetails || [];
                  const buckets = [
                        { label: '0–10h', min: 0, max: 10 },
                        { label: '10–20h', min: 10, max: 20 },
                        { label: '20–30h', min: 20, max: 30 },
                        { label: '30–40h', min: 30, max: 40 },
                        { label: '40h+', min: 40, max: 999 },
                      ];
                  const getHours = (u) => u.weeklyHours ?? (u.dailyHours != null ? u.dailyHours * 5 : 0);
                  const counts = buckets.map((b) => details.filter((u) => { const h = getHours(u); return h >= b.min && h < b.max; }).length);
                  const hasData = counts.some((c) => c > 0);
                  return hasData ? (
                    <div className="h-48 sm:h-56 md:h-64">
                      <Bar
                        data={{
                          labels: buckets.map((b) => b.label),
                          datasets: [{ label: 'Users', data: counts, backgroundColor: '#0EA5E9', borderRadius: 6 }],
                        }}
options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} users` } } },
                        scales: { y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Users' } }, x: { title: { display: true, text: 'Hours (period)' } } },
                        }, chartColors)}
                      />
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-secondary-500">
                      <div className="text-center">
                        <Users className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
                        <p>No user hours data</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Week-over-week KPI cards (when comparing to previous period) */}
            {comparePrevious && previousTrendsData?.hoursByDay?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4">
                  <p className="text-xs font-medium text-secondary-600 mb-1">This Period Hours</p>
                  <p className="text-xl font-bold text-primary-600">
                    {(trendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0).toFixed(1)}h
                  </p>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4">
                  <p className="text-xs font-medium text-secondary-600 mb-1">Previous Period Hours</p>
                  <p className="text-xl font-bold text-secondary-600">
                    {(previousTrendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0).toFixed(1)}h
                  </p>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4">
                  <p className="text-xs font-medium text-secondary-600 mb-1">Change</p>
                  <p className={`text-xl font-bold ${(() => {
                    const curr = (trendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0);
                    const prev = (previousTrendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0);
                    const pct = prev > 0 ? ((curr - prev) / prev * 100) : 0;
                    return pct >= 0 ? 'text-green-600' : 'text-red-600';
                  })()}`}>
                    {(() => {
                      const curr = (trendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0);
                      const prev = (previousTrendsData?.hoursByDay || []).reduce((s, d) => s + (d.hours || 0), 0);
                      const pct = prev > 0 ? ((curr - prev) / prev * 100) : 0;
                      return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
                    })()}
                  </p>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4">
                  <p className="text-xs font-medium text-secondary-600 mb-1">Active Users (avg/day)</p>
                  <p className="text-xl font-bold text-indigo-600">
                    {trendsData?.activeUsersByDay?.length
                      ? (trendsData.activeUsersByDay.reduce((s, d) => s + (d.count || 0), 0) / trendsData.activeUsersByDay.length).toFixed(1)
                      : '0'}
                  </p>
                </div>
              </div>
            )}

            {/* Peak hours + Projects/Tasks created */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Peak Hours (by hour of day)</h3>
                {trendsLoading ? (
                  <SectionLoader height="h-56" />
                ) : trendsData?.hoursByHour?.length > 0 ? (
                  <div className="h-56">
                    <Bar
                      data={{
                        labels: (trendsData.hoursByHour || []).map((d) => `${d.hour}:00`),
                        datasets: [{ label: 'Hours', data: (trendsData.hoursByHour || []).map((d) => d.hours), backgroundColor: '#3B82F6', borderRadius: 4 }],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y}h` } } },
                        scales: { y: { beginAtZero: true, title: { display: true, text: 'Hours' } }, x: { ticks: { maxTicksLimit: 12 } } },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-56 flex items-center justify-center text-secondary-500">
                    <Clock className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                    <p className="text-sm">No time data</p>
                  </div>
                )}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Projects Created</h3>
                {trendsLoading ? (
                  <SectionLoader height="h-56" />
                ) : trendsData?.projectsCreatedByDay?.length > 0 ? (
                  <div className="h-56">
                    <Bar
                      data={{
                        labels: (trendsData.projectsCreatedByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                        datasets: [{ label: 'Projects', data: (trendsData.projectsCreatedByDay || []).map((d) => d.count), backgroundColor: '#8B5CF6', borderRadius: 4 }],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} created` } } },
                        scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } }, x: { ticks: { maxRotation: 45, maxTicksLimit: 10 } } },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-56 flex items-center justify-center text-secondary-500">
                    <FolderOpen className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                    <p className="text-sm">No projects created in period</p>
                  </div>
                )}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Tasks Created</h3>
                {trendsLoading ? (
                  <SectionLoader height="h-56" />
                ) : trendsData?.tasksCreatedByDay?.length > 0 ? (
                  <div className="h-56">
                    <Bar
                      data={{
                        labels: (trendsData.tasksCreatedByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                        datasets: [{ label: 'Tasks', data: (trendsData.tasksCreatedByDay || []).map((d) => d.count), backgroundColor: '#10B981', borderRadius: 4 }],
                      }}
                      options={mergeChartOptions({
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} created` } } },
                        scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } }, x: { ticks: { maxRotation: 45, maxTicksLimit: 10 } } },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-56 flex items-center justify-center text-secondary-500">
                    <CheckSquare className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                    <p className="text-sm">No tasks created in period</p>
                  </div>
                )}
              </div>
            </div>

            {/* Efficiency by member + Project idle % + Overdue summary + All users by hours + Task efficiency + Productive vs idle */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Efficiency by Member</h3>
                {isLoading ? (
                  <SectionLoader height="h-56" />
                ) : (() => {
                  const withEff = (analytics?.userDetails || []).filter((u) => (u.efficiency ?? 0) > 0).sort((a, b) => (b.efficiency ?? 0) - (a.efficiency ?? 0)).slice(0, 10);
                  return withEff.length > 0 ? (
                    <div className="h-56">
                      <Bar
                        data={{
                          labels: withEff.map((u) => (u.name && u.name.length > 16 ? u.name.slice(0, 14) + '…' : u.name) || 'Unknown'),
                          datasets: [{ label: 'Efficiency %', data: withEff.map((u) => Number((u.efficiency ?? 0).toFixed(1))), backgroundColor: '#0EA5E9', borderRadius: 6 }],
                        }}
                        options={mergeChartOptions({
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}%` } } },
                          scales: { x: { beginAtZero: true, max: 100 } },
                        }, chartColors)}
                      />
                    </div>
                  ) : (
                    <div className="h-56 flex items-center justify-center text-secondary-500">
                      <Zap className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                      <p className="text-sm">No efficiency data</p>
                    </div>
                  );
                })()}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Project Idle %</h3>
                {isLoading ? (
                  <SectionLoader height="h-56" />
                ) : (() => {
                  const withIdle = (analytics?.projectAnalytics || []).filter((p) => (p.idlePercentage ?? 0) > 0).sort((a, b) => (b.idlePercentage ?? 0) - (a.idlePercentage ?? 0)).slice(0, 8);
                  return withIdle.length > 0 ? (
                    <div className="h-56">
                      <Bar
                        data={{
                          labels: withIdle.map((p) => (p.name && p.name.length > 16 ? p.name.slice(0, 14) + '…' : p.name) || 'Unknown'),
                          datasets: [{ label: 'Idle %', data: withIdle.map((p) => Number((p.idlePercentage ?? 0).toFixed(1))), backgroundColor: '#F59E0B', borderRadius: 6 }],
                        }}
                        options={mergeChartOptions({
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}% idle` } } },
                          scales: { x: { beginAtZero: true, max: 100 } },
                        }, chartColors)}
                      />
                    </div>
                  ) : (
                    <div className="h-56 flex items-center justify-center text-secondary-500">
                      <Gauge className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                      <p className="text-sm">No project idle data</p>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Overdue / Due today / High priority summary */}
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Task Pressure Summary</h3>
              {isLoading ? (
                <SectionLoader height="h-40" />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-rose-50 dark:bg-rose-900/30 rounded-lg border border-rose-200/50 dark:border-rose-800/30">
                    <AlertTriangle className="h-8 w-8 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">Overdue</p>
                      <p className="text-2xl font-bold text-rose-800 dark:text-rose-200">{analytics?.overdueTasks ?? analytics?.overdueTasksList?.length ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                    <Calendar className="h-8 w-8 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">Due Today</p>
                      <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">{analytics?.dueTodayTasks?.length ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
                    <Target className="h-8 w-8 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-orange-700 dark:text-orange-300 font-medium">High Priority</p>
                      <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">{analytics?.highPriorityTasks?.length ?? 0}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* All users by hours + Task efficiency (top tasks) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">All Users by Hours</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : (() => {
                  const details = (analytics?.userDetails || []).sort((a, b) => (b.weeklyHours ?? 0) - (a.weeklyHours ?? 0)).slice(0, 12);
                  const getH = (u) => u.weeklyHours ?? (u.dailyHours != null ? u.dailyHours * 5 : 0);
                  return details.length > 0 ? (
                    <div className="h-48 sm:h-56 md:h-64">
                      <Bar
                        data={{
                          labels: details.map((u) => (u.name && u.name.length > 16 ? u.name.slice(0, 14) + '…' : u.name) || 'Unknown'),
                          datasets: [{ label: 'Hours', data: details.map(getH), backgroundColor: '#6366F1', borderRadius: 6 }],
                        }}
                        options={mergeChartOptions({
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}h` } } },
                          scales: { x: { beginAtZero: true } },
                        }, chartColors)}
                      />
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-secondary-500">
                      <Users className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                      <p className="text-sm">No user data</p>
                    </div>
                  );
                })()}
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Top Tasks by Hours</h3>
                {isLoading ? (
                  <SectionLoader height="h-64" />
                ) : (analytics?.taskEfficiency?.length ?? 0) > 0 ? (
                  <div className="h-48 sm:h-56 md:h-64">
                    <Bar
                      data={{
                        labels: (analytics.taskEfficiency || []).slice(0, 10).map((t) => (t.task_name && t.task_name.length > 20 ? t.task_name.slice(0, 18) + '…' : t.task_name) || 'Task'),
                        datasets: [{ label: 'Hours', data: (analytics.taskEfficiency || []).slice(0, 10).map((t) => t.actual_hours ?? t.hours ?? 0), backgroundColor: '#14B8A6', borderRadius: 6 }],
                      }}
                      options={mergeChartOptions({
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}h` } } },
                        scales: { x: { beginAtZero: true } },
                      }, chartColors)}
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-secondary-500">
                    <Target className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                    <p className="text-sm">No task data</p>
                  </div>
                )}
              </div>
            </div>

            {/* Productive vs Idle stacked area */}
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-4">Productive vs Idle (Stacked)</h3>
              {trendsLoading ? (
                <SectionLoader height="h-56" />
              ) : trendsData?.hoursByDay?.length > 0 && trendsData?.idleHoursByDay?.length > 0 ? (
                <div className="h-56">
                  <Line
                    data={{
                      labels: (trendsData.hoursByDay || []).map((d) => format(parseISO(d.date), 'MMM d')),
                      datasets: [
                        {
                          label: 'Productive',
                          data: (trendsData.hoursByDay || []).map((d) => {
                            const idle = trendsData.idleHoursByDay.find((x) => x.date === d.date);
                            return Math.max(0, (d.hours || 0) - (idle?.hours ?? 0));
                          }),
                          borderColor: '#10B981',
                          backgroundColor: 'rgba(16, 185, 129, 0.5)',
                          fill: true,
                          tension: 0.3,
                        },
                        {
                          label: 'Idle',
                          data: (trendsData.hoursByDay || []).map((d) => {
                            const idle = trendsData.idleHoursByDay.find((x) => x.date === d.date);
                            return idle?.hours ?? 0;
                          }),
                          borderColor: '#F59E0B',
                          backgroundColor: 'rgba(245, 158, 11, 0.5)',
                          fill: true,
                          tension: 0.3,
                        },
                      ],
                    }}
                    options={mergeChartOptions({
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: 'top' }, tooltip: { mode: 'index', intersect: false } },
                      scales: { y: { stacked: true, beginAtZero: true }, x: { stacked: true, ticks: { maxTicksLimit: 12 } } },
                    }, chartColors)}
                  />
                </div>
              ) : (
                <div className="h-56 flex items-center justify-center text-secondary-500">
                  <Activity className="h-10 w-10 mx-auto mb-2 text-secondary-400" />
                  <p className="text-sm">No time data</p>
                </div>
              )}
            </div>
          </div>

          {/* Performance Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">User Performance</h3>
              {isLoading ? (
                <SectionLoader height="h-32" />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-600">Average Daily Hours</span>
                    <span className="font-medium">{analytics?.avgDailyHours?.toFixed(1) || '0.0'}h / 8h</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(((analytics?.avgDailyHours || 0) / dailyTargetHours) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-600">
                    Average {getPeriodDisplayName(selectedPeriod)} Hours
                  </span>
                    <span className="font-medium">{analytics?.avgWeeklyHours?.toFixed(1) || '0.0'}h / {getTargetHours()}h</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(((analytics?.avgWeeklyHours || 0) / getTargetHours()) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Task Status Distribution</h3>
              <div className="space-y-3">
                {analytics?.taskStatusDistribution && analytics?.taskStatusDistribution?.length > 0 ? (
                  analytics?.taskStatusDistribution?.map((status) => (
                    <div 
                      key={status.name} 
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 hover:border-secondary-300 border border-transparent cursor-pointer transition-all duration-200 group"
                      onClick={() => navigate(`/tasks?filter=status&status=${encodeURIComponent(status.name)}`)}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: status.color.replace('bg-', '').split('-')[0] }}
                        ></div>
                        <span className="text-sm text-secondary-600 group-hover:text-secondary-900 transition-colors">{status.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{status.count}</span>
                        <span className="text-secondary-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          →
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                    <p className="text-secondary-600 text-sm">No tasks yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Performers and Issues */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Top Performers</h3>
              {isLoading ? (
                <SectionLoader height="h-48" />
              ) : (
                <div className="space-y-3">
                {(() => {
                  const filteredPerformers = analytics?.topPerformers?.filter(performer => performer.hours >= 1) || [];
                  const paginatedPerformers = paginateArray(
                    filteredPerformers, 
                    pagination.topPerformers.currentPage, 
                    pagination.topPerformers.itemsPerPage
                  );
                  return filteredPerformers.length > 0 ? (
                    <>
                      {paginatedPerformers.map((performer, index) => {
                        const actualIndex = (pagination.topPerformers.currentPage - 1) * pagination.topPerformers.itemsPerPage + index;
                        return (
                          <div key={performer.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-600">{actualIndex + 1}</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-secondary-900">{performer.name}</p>
                                <p className="text-sm text-secondary-600">{performer.hours.toFixed(1)}h tracked</p>
                              </div>
                            </div>
                            <div className={`flex items-center gap-1 ${getPerformanceColor(performer.performance)}`}>
                              {getPerformanceIcon(performer.performance)}
                              <span className="text-sm font-medium">{performer.performance}%</span>
                            </div>
                          </div>
                        );
                      })}
                      <Pagination
                        currentPage={pagination.topPerformers.currentPage}
                        totalPages={Math.ceil(filteredPerformers.length / pagination.topPerformers.itemsPerPage)}
                        onPageChange={(page) => updatePagination('topPerformers', page)}
                        totalItems={filteredPerformers.length}
                        itemsPerPage={pagination.topPerformers.itemsPerPage}
                      />
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <TrendingUp className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                      <p className="text-secondary-600 text-sm">No performers yet</p>
                    </div>
                  );
                })()}
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Performance Issues</h3>
              {isLoading ? (
                <SectionLoader height="h-48" />
              ) : (
                <div className="space-y-3">
                {(() => {
                  const paginatedIssues = paginateArray(
                    analytics?.performanceIssues || [], 
                    pagination.performanceIssues.currentPage, 
                    pagination.performanceIssues.itemsPerPage
                  );
                  return (
                    <>
                      {paginatedIssues.map((issue, index) => (
                        <div key={issue.id} className="flex items-center justify-between p-3 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200/50 dark:border-rose-800/30">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-rose-100 dark:bg-rose-900/40 rounded-full flex items-center justify-center">
                              <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">{issue.name}</p>
                              <p className="text-sm text-secondary-600 dark:text-secondary-400">{issue.issue}</p>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-rose-600 dark:text-rose-400">{issue.hours.toFixed(1)}h</span>
                        </div>
                      ))}
                      <Pagination
                        currentPage={pagination.performanceIssues.currentPage}
                        totalPages={Math.ceil((analytics?.performanceIssues?.length || 0) / pagination.performanceIssues.itemsPerPage)}
                        onPageChange={(page) => updatePagination('performanceIssues', page)}
                        totalItems={analytics?.performanceIssues?.length || 0}
                        itemsPerPage={pagination.performanceIssues.itemsPerPage}
                      />
                    </>
                  );
                })()}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Users View */}
      {selectedView === 'users' && (
        <>
          <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">User Performance Details</h3>
                {!isLoading && userFilter !== 'all' && (
                  <p className="text-sm text-secondary-600 mt-1">
                    Showing {userFilter === 'overloaded' ? 'overloaded' : userFilter === 'underutilized' ? 'underutilized' : 'normal'} users 
                    ({getFilteredUsers().length} of {analytics?.userDetails?.length || 0})
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <select
                  className="input-field w-40 border-secondary-300 focus:ring-primary-500"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                >
                  <option value="all">All Users</option>
                  <option value="overloaded">Overloaded Users</option>
                  <option value="underutilized">Underutilized Users</option>
                  <option value="normal">Normal Users</option>
                </select>
                {userFilter !== 'all' && (
                  <button
                    onClick={() => setUserFilter('all')}
                    className="btn-secondary text-sm px-3 py-2"
                  >
                    View All Users
                  </button>
                )}
                <button
                  onClick={handleExportUsersCSV}
                  className="btn-secondary text-sm px-3 py-2"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-orange-600">Overloaded</p>
                    {isLoading ? (
                      <div className="animate-pulse bg-orange-200 h-8 w-12 rounded"></div>
                    ) : (
                      <p className="text-2xl font-bold text-orange-700">{analytics?.overloadedUsers || 0}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <TrendingDown className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-600">Underutilized</p>
                    {isLoading ? (
                      <div className="animate-pulse bg-blue-200 h-8 w-12 rounded"></div>
                    ) : (
                      <p className="text-2xl font-bold text-blue-700">{analytics?.underutilizedUsers || 0}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600">Normal</p>
                    {isLoading ? (
                      <div className="animate-pulse bg-green-200 h-8 w-12 rounded"></div>
                    ) : (
                      <p className="text-2xl font-bold text-green-700">
                        {(analytics?.userDetails?.length || 0) - (analytics?.overloadedUsers || 0) - (analytics?.underutilizedUsers || 0)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-secondary-100 rounded-full">
                    <Users className="h-5 w-5 text-secondary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-600">Total</p>
                    {isLoading ? (
                      <div className="animate-pulse bg-secondary-200 h-8 w-12 rounded"></div>
                    ) : (
                      <p className="text-2xl font-bold text-secondary-700">{analytics?.userDetails?.length || 0}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {isLoading ? (
              <SectionLoader height="h-64" />
            ) : (
              <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">User</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Daily Hours</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">
                    {getPeriodDisplayName(selectedPeriod)} Hours
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle Hours</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle %</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Performance</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Efficiency</th>
                        <th className="text-center py-3 px-4 font-medium text-secondary-900">
                          <div className="flex items-center justify-center gap-1">
                            Tasks
                          </div>
                        </th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                    const filteredUsers = getFilteredUsers();
                    
                    if (filteredUsers.length === 0) {
                      return (
                        <tr>
                          <td colSpan="10" className="py-12 text-center">
                            <div className="flex flex-col items-center">
                              <Users className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                              <h4 className="text-lg font-medium text-secondary-900 mb-2">No users found</h4>
                              <p className="text-secondary-600 text-sm mb-3">
                                {userFilter === 'all' 
                                  ? 'No users are currently available for the selected period.'
                                  : `No ${userFilter} users found for the selected period.`
                                }
                              </p>
                              {userFilter !== 'all' && (
                                <button
                                  onClick={() => setUserFilter('all')}
                                  className="btn-primary text-sm px-4 py-2"
                                >
                                  View All Users
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    
                  const paginatedUsers = paginateArray(
                      filteredUsers, 
                    pagination.userDetails.currentPage, 
                    pagination.userDetails.itemsPerPage
                  );
                  return paginatedUsers.map((user) => (
                      <tr key={user.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-secondary-900">{user.name}</p>
                            <p className="text-sm text-secondary-600">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${user.dailyHours < 6 ? 'text-red-600' : 'text-green-600'}`}>
                          {user.dailyHours.toFixed(1)}h
                        </span>
                      </td>
                      <td className="py-3 px-4">
                          <div className="space-y-1">
                            <span className={`font-medium ${user.weeklyHours < ((user.targetHours ?? getTargetHours()) * 0.75) ? 'text-red-600' : user.weeklyHours > (user.targetHours ?? getTargetHours()) ? 'text-orange-600' : 'text-green-600'}`}>
                          {user.weeklyHours.toFixed(1)}h
                        </span>
                            <div className="w-full bg-secondary-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${
                                  user.weeklyHours < ((user.targetHours ?? getTargetHours()) * 0.75) ? 'bg-red-500' : 
                                  user.weeklyHours > (user.targetHours ?? getTargetHours()) ? 'bg-orange-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min((user.weeklyHours / ((user.targetHours ?? getTargetHours()) || 1)) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-secondary-500">
                              Target: {(user.targetHours ?? getTargetHours()).toFixed(0)}h/{getTargetPeriodLabel()}
                              {(() => {
                                const parts = [];
                                if (user.holidayDays > 0) {
                                  parts.push(`${user.holidayDays} holiday${user.holidayDays === 1 ? '' : 's'}`);
                                }
                                if (user.leaveDays > 0) {
                                  parts.push(`${user.leaveDays} leave`);
                                }
                                return parts.length ? ` · ${parts.join(', ')}` : '';
                              })()}
                            </p>
                          </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${user.idleHours > 5 ? 'text-red-600' : user.idleHours > 2 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {user.idleHours?.toFixed(1) || 0}h
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${user.idlePercentage > 30 ? 'text-red-600' : user.idlePercentage > 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {user.idlePercentage?.toFixed(1) || 0}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getPerformanceIcon(user.performance)}
                          <span className={`font-medium ${getPerformanceColor(user.performance)}`}>
                            {user.performance}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getEfficiencyIcon(user.efficiency)}
                          <span className={`font-medium ${getEfficiencyColor(user.efficiency)}`}>
                            {user.efficiency != null ? `${user.efficiency}%` : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col items-center justify-center">
                          <button
                            onClick={() => navigate(`/tasks?filter=assigned&user=${encodeURIComponent(user.id)}`)}
                            className="hover:bg-secondary-50 rounded px-2 py-1 transition-colors duration-200 cursor-pointer group"
                            title="Click to view tasks assigned to this user"
                          >
                            <div className="flex items-center justify-center gap-1">
                              <span className="font-medium text-primary-600 group-hover:text-primary-700 underline decoration-dotted underline-offset-2">
                                {user.activeTasks}
                              </span>
                              <ExternalLink className="h-3 w-3 text-secondary-400 group-hover:text-secondary-600" />
                            </div>
                            {user.completedTasks > 0 && (
                              <div className="text-xs text-secondary-500 text-center">
                                +{user.completedTasks} completed
                              </div>
                            )}
                          </button>
                          </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.utilizationStatus === 'overloaded' ? 'bg-orange-100 text-orange-800' :
                            user.utilizationStatus === 'underutilized' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {user.utilizationStatus === 'overloaded' ? 'Overloaded' :
                             user.utilizationStatus === 'underutilized' ? 'Underutilized' : 'Normal'}
                        </span>
                      </td>
                              
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
            <Pagination
              currentPage={pagination.userDetails.currentPage}
                totalPages={Math.ceil(getFilteredUsers().length / pagination.userDetails.itemsPerPage)}
              onPageChange={(page) => updatePagination('userDetails', page)}
                totalItems={getFilteredUsers().length}
              itemsPerPage={pagination.userDetails.itemsPerPage}
            />
              </div>
            )}
          </div>
        </>
      )}

      {/* Efficiency View */}
      {selectedView === 'efficiency' && (
        <>
          <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">Task Efficiency Analysis</h3>
              <p className="text-xs text-secondary-500 italic">Click on task names to view details</p>
            </div>
            
            {isLoading ? (
              <SectionLoader height="h-64" />
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Task</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Assigned User</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Estimated Hours</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Actual Hours</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Efficiency</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-900">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    if (!analytics?.taskEfficiency || analytics?.taskEfficiency?.length === 0) {
                      return (
                        <tr>
                          <td colSpan="6" className="py-12 text-center">
                            <div className="flex flex-col items-center">
                              <CheckSquare className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                              <h4 className="text-lg font-medium text-secondary-900 mb-2">No tasks found</h4>
                              <p className="text-secondary-600 text-sm">There are no tasks to display for efficiency analysis.</p>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    
                    const paginatedTasks = paginateArray(
                      analytics?.taskEfficiency || [], 
                      pagination.taskEfficiency.currentPage, 
                      pagination.taskEfficiency.itemsPerPage
                    );
                    
                    return paginatedTasks.map((task) => (
                      <tr key={task.task_id} className="border-b border-secondary-100">
                        <td className="py-3 px-4">
                          <p 
                            className="text-sm font-medium text-secondary-900 cursor-pointer hover:text-primary-600 hover:underline transition-all duration-200 flex items-center gap-2 group"
                            onClick={() => navigate(`/tasks/${task.task_id}`)}
                          >
                            {task.task_name}
                            <ExternalLink className="h-3 w-3 text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-secondary-600">{task.assigned_to}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium">{task.estimated_hours || 'N/A'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium">{task.actual_hours}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getEfficiencyIcon(task.efficiency)}
                            <span className={`font-medium ${getEfficiencyColor(task.efficiency)}`}>
                              {task.efficiency != null ? `${task.efficiency}%` : 'N/A'}
                            </span>
                            {task.completedLate && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1 ml-2">
                                <AlertTriangle className="h-4 w-4" />
                                Completed Late
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            !task.efficiency ? 'bg-gray-100 text-gray-800' :
                            task.efficiency >= 100 ? 'bg-green-100 text-green-800' :
                            task.efficiency >= 80 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {!task.efficiency ? 'No Data' :
                             task.efficiency >= 100 ? 'Ahead' :
                             task.efficiency >= 80 ? 'On Track' : 'Behind'}
                          </span>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
              {analytics?.taskEfficiency && analytics?.taskEfficiency?.length > 0 && (
                <Pagination
                  currentPage={pagination.taskEfficiency.currentPage}
                  totalPages={Math.ceil((analytics?.taskEfficiency?.length || 0) / pagination.taskEfficiency.itemsPerPage)}
                  onPageChange={(page) => updatePagination('taskEfficiency', page)}
                  totalItems={analytics?.taskEfficiency?.length || 0}
                  itemsPerPage={pagination.taskEfficiency.itemsPerPage}
                />
              )}
              </div>
            )}
          </div>

          {/* High Idle Entries Section */}
          {!isLoading && analytics?.highIdleEntries && analytics?.highIdleEntries?.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">High Idle Time Entries (&gt;20% idle)</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-secondary-200">
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">User</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Project</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Task</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Description</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Total Hours</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle Hours</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle %</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-900">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const paginatedEntries = paginateArray(
                        analytics?.highIdleEntries || [], 
                        pagination.highIdleEntries.currentPage, 
                        pagination.highIdleEntries.itemsPerPage
                      );
                      return paginatedEntries.map((entry) => (
                        <tr key={entry.id} className="border-b border-secondary-100">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-secondary-900">{entry.user}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-secondary-600">{entry.project}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-secondary-600">{entry.task}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-secondary-600 max-w-xs truncate block" title={entry.description}>
                              {entry.description}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium">{entry.totalHours}h</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium text-red-600">{entry.idleHours}h</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`font-medium ${entry.idlePercentage > 50 ? 'text-red-600' : 'text-orange-600'}`}>
                              {entry.idlePercentage}%
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-secondary-600">{entry.date}</span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                {analytics?.highIdleEntries && analytics?.highIdleEntries?.length > 0 && (
                  <Pagination
                    currentPage={pagination.highIdleEntries.currentPage}
                    totalPages={Math.ceil((analytics?.highIdleEntries?.length || 0) / pagination.highIdleEntries.itemsPerPage)}
                    onPageChange={(page) => updatePagination('highIdleEntries', page)}
                    totalItems={analytics?.highIdleEntries?.length || 0}
                    itemsPerPage={pagination.highIdleEntries.itemsPerPage}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Idle Detection View */}
      {selectedView === 'idle' && (
        <>
          {/* Period Loading Overlay - Removed to allow progressive loading */}
          
          <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Idle Detection Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-sm text-secondary-600">Total Idle Time</p>
                <p className="text-lg font-semibold text-purple-600">{formatDuration(analytics?.totalIdleHours || 0)}</p>
                <p className="text-xs text-secondary-600">{analytics?.totalIdlePercentage?.toFixed(1) || 0}% of tracked time</p>
              </div>
              <div className="group">
                <p className="text-xs text-secondary-600 mb-2">Idle Time Trend (This Period)</p>
                <div className="w-full h-24 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg p-4 border border-secondary-200 dark:border-secondary-600 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all duration-300 cursor-pointer group-hover:scale-[1.02]">
                  <div className="h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-secondary-600 dark:text-secondary-400 font-medium">Idle Time</span>
                      <span className="font-bold text-purple-600 text-sm group-hover:text-purple-700 transition-colors">
                        {analytics?.totalIdlePercentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    
                    <div className="relative w-full bg-secondary-200 rounded-full h-3 overflow-hidden group-hover:h-4 transition-all duration-300">
                      <div 
                        className="bg-purple-600 dark:bg-purple-500 h-full rounded-full transition-all duration-500 ease-out shadow-sm" 
                        style={{ width: `${Math.min(analytics?.totalIdlePercentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs mt-2">
                      <span className="text-secondary-600 dark:text-secondary-400 font-medium">Productive Time</span>
                      <span className="font-bold text-green-600 text-sm group-hover:text-green-700 transition-colors">
                        {100 - (analytics?.totalIdlePercentage || 0)}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 text-center">
                  {analytics?.totalIdlePercentage > 0 ? (
                    <div className="flex items-center justify-center gap-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-all duration-300 group-hover:scale-105">
                      <AlertTriangle className="h-4 w-4 text-orange-500 group-hover:animate-bounce" />
                      <span className="text-xs text-orange-700 font-semibold">
                        {analytics?.totalIdlePercentage?.toFixed(1) || 0}% Idle Time Detected
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all duration-300 group-hover:scale-105">
                      <CheckCircle className="h-4 w-4 text-green-500 group-hover:animate-pulse" />
                      <span className="text-xs text-green-700 font-semibold">100% Productive - Great Job!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-secondary-900 dark:text-white mb-2">High Idle Time Entries (&gt;20% idle)</h4>
              {analytics?.highIdleEntries && analytics?.highIdleEntries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200">
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">User</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Project</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Task</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Description</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Total Time</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle Time</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Idle %</th>
                        <th className="text-left py-3 px-4 font-medium text-secondary-900">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const paginatedEntries = paginateArray(
                          analytics?.highIdleEntries || [], 
                          pagination.highIdleEntries.currentPage, 
                          pagination.highIdleEntries.itemsPerPage
                        );
                        return paginatedEntries.map((entry) => (
                          <tr key={entry.id} className="border-b border-secondary-100">
                            <td className="py-3 px-4">
                              <span className="text-sm font-medium text-secondary-900">{entry.user}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-secondary-600">{entry.project}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-secondary-600">{entry.task}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-secondary-600 max-w-xs truncate block" title={entry.description}>
                                {entry.description}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium">{formatDuration(entry.totalHours)}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-red-600">{formatDuration(entry.idleHours)}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`font-medium ${entry.idlePercentage > 50 ? 'text-red-600' : 'text-orange-600'}`}>
                                {entry.idlePercentage}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-secondary-600">{entry.date}</span>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-secondary-600">No high idle entries for this period.</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tasks View */}
      {selectedView === 'tasks' && (
        <>
          {/* Period Loading Overlay - Removed to allow progressive loading */}
          
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary-900">User Task Analytics</h3>
            <p className="text-xs text-secondary-500 italic">Click on any task card to view details</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-secondary-50 rounded-lg">
              <h4 className="font-medium text-secondary-900 mb-2">Overdue Tasks</h4>
              <div className="space-y-2">
                {(() => {
                  if (!analytics?.overdueTasksList || analytics?.overdueTasksList?.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm text-secondary-600">No overdue tasks</p>
                      </div>
                    );
                  }
                  
                  const paginatedTasks = paginateArray(
                    analytics?.overdueTasksList || [], 
                    pagination.overdueTasks.currentPage, 
                    pagination.overdueTasks.itemsPerPage
                  );
                  
                  return (
                    <>
                      {paginatedTasks.map((task) => (
                        <div 
                          key={task.id} 
                          className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800/40 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:shadow-md transition-all duration-200 group"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-secondary-900 dark:text-secondary-100 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">{task.name}</p>
                              <p className="text-sm text-secondary-600 dark:text-secondary-400">Assigned user: {task.assignedTo}</p>
                              <p className="text-sm text-rose-600 dark:text-rose-400">
                                {differenceInDays(new Date(), new Date(task.dueDate))} days overdue
                              </p>
                            </div>
                            <ExternalLink className="h-4 w-4 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                        </div>
                      ))}
                      <Pagination
                        currentPage={pagination.overdueTasks.currentPage}
                        totalPages={Math.ceil((analytics?.overdueTasksList?.length || 0) / pagination.overdueTasks.itemsPerPage)}
                        onPageChange={(page) => updatePagination('overdueTasks', page)}
                        totalItems={analytics?.overdueTasksList?.length || 0}
                        itemsPerPage={pagination.overdueTasks.itemsPerPage}
                      />
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="p-4 bg-secondary-50 rounded-lg">
              <h4 className="font-medium text-secondary-900 mb-2">Due Today</h4>
              <div className="space-y-2">
                {(() => {
                  if (!analytics?.dueTodayTasks || analytics?.dueTodayTasks?.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <Calendar className="h-8 w-8 text-violet-500 mx-auto mb-2" />
                        <p className="text-sm text-secondary-600">No tasks due today</p>
                      </div>
                    );
                  }
                  
                  const paginatedTasks = paginateArray(
                    analytics?.dueTodayTasks || [], 
                    pagination.dueTodayTasks.currentPage, 
                    pagination.dueTodayTasks.itemsPerPage
                  );
                  
                  return (
                    <>
                      {paginatedTasks.map((task) => (
                        <div 
                          key={task.id} 
                          className="p-3 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 hover:shadow-md transition-all duration-200 group"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-secondary-900 group-hover:text-orange-700 transition-colors">{task.name}</p>
                              <p className="text-sm text-secondary-600">Assigned user: {task.assignedTo}</p>
                              <p className="text-sm text-orange-600">Due today</p>
                            </div>
                            <ExternalLink className="h-4 w-4 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                        </div>
                      ))}
                      <Pagination
                        currentPage={pagination.dueTodayTasks.currentPage}
                        totalPages={Math.ceil((analytics?.dueTodayTasks?.length || 0) / pagination.dueTodayTasks.itemsPerPage)}
                        onPageChange={(page) => updatePagination('dueTodayTasks', page)}
                        totalItems={analytics?.dueTodayTasks?.length || 0}
                        itemsPerPage={pagination.dueTodayTasks.itemsPerPage}
                      />
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="p-4 bg-secondary-50 rounded-lg">
              <h4 className="font-medium text-secondary-900 mb-2">High Priority Tasks</h4>
              <div className="space-y-2">
                {(() => {
                  if (!analytics?.highPriorityTasks || analytics?.highPriorityTasks?.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-secondary-600">No high priority tasks</p>
                      </div>
                    );
                  }
                  
                  const paginatedTasks = paginateArray(
                    analytics?.highPriorityTasks || [], 
                    pagination.highPriorityTasks.currentPage, 
                    pagination.highPriorityTasks.itemsPerPage
                  );
                  
                  return (
                    <>
                      {paginatedTasks.map((task) => (
                        <div 
                          key={task.id} 
                          className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 hover:shadow-md transition-all duration-200 group"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-secondary-900 group-hover:text-yellow-700 transition-colors">{task.name}</p>
                              <p className="text-sm text-secondary-600">Assigned user: {task.assignedTo}</p>
                              <p className="text-sm text-yellow-600">Priority: {task.priority}</p>
                            </div>
                            <ExternalLink className="h-4 w-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                        </div>
                      ))}
                      <Pagination
                        currentPage={pagination.highPriorityTasks.currentPage}
                        totalPages={Math.ceil((analytics?.highPriorityTasks?.length || 0) / pagination.highPriorityTasks.itemsPerPage)}
                        onPageChange={(page) => updatePagination('highPriorityTasks', page)}
                        totalItems={analytics?.highPriorityTasks?.length || 0}
                        itemsPerPage={pagination.highPriorityTasks.itemsPerPage}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Projects View */}
      {selectedView === 'projects' && (
        <>
          {/* Period Loading Overlay - Removed to allow progressive loading */}
          
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">User Project Analytics</h3>
          <div className="space-y-4">
            {(() => {
              if (!analytics?.projectAnalytics || analytics?.projectAnalytics?.length === 0) {
                return (
                  <div className="text-center py-12">
                    <FolderOpen className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-secondary-900 mb-2">No projects found</h4>
                    <p className="text-secondary-600 text-sm mb-4">There are no projects to display for the selected period.</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={() => setSelectedView('overview')}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        View Overview
                      </button>
                      <button
                        onClick={() => navigate('/projects')}
                        className="px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        Go to Projects
                      </button>
                    </div>
                  </div>
                );
              }
              
              const paginatedProjects = paginateArray(
                analytics?.projectAnalytics || [], 
                pagination.projectAnalytics.currentPage, 
                pagination.projectAnalytics.itemsPerPage
              );
              
              return (
                <>
                  {paginatedProjects.map((project) => (
                    <div key={project.id} className="p-4 border border-secondary-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-secondary-900">{project.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.progress >= 80 ? 'bg-green-100 text-green-800' :
                          project.progress >= 50 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {project.progress}% Complete
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                        <div>
                          <p className="text-secondary-600">Total Hours</p>
                          <p className="font-medium">{project.totalHours.toFixed(1)}h</p>
                        </div>
                        <div>
                          <p className="text-secondary-600">Active Tasks</p>
                          <p className="font-medium">{project.activeTasks}</p>
                        </div>
                        <div>
                          <p className="text-secondary-600">Completed Tasks</p>
                          <p className="font-medium">{project.completedTasks}</p>
                        </div>
                        <div>
                          <p className="text-secondary-600">Overdue Tasks</p>
                          <p className="font-medium text-rose-600 dark:text-rose-400">{project.overdueTasks}</p>
                        </div>
                        <div>
                          <p className="text-secondary-600">Idle Hours</p>
                          <p className={`font-medium ${project.idleHours > 5 ? 'text-red-600' : project.idleHours > 2 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {project.idleHours?.toFixed(1) || 0}h
                          </p>
                        </div>
                        <div>
                          <p className="text-secondary-600">Idle %</p>
                          <p className={`font-medium ${project.idlePercentage > 30 ? 'text-red-600' : project.idlePercentage > 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {project.idlePercentage?.toFixed(1) || 0}%
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="w-full bg-secondary-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Pagination
                    currentPage={pagination.projectAnalytics.currentPage}
                    totalPages={Math.ceil((analytics?.projectAnalytics?.length || 0) / pagination.projectAnalytics.itemsPerPage)}
                    onPageChange={(page) => updatePagination('projectAnalytics', page)}
                    totalItems={analytics?.projectAnalytics?.length || 0}
                    itemsPerPage={pagination.projectAnalytics.itemsPerPage}
                  />
                </>
              );
            })()}
          </div>
        </div>
        </>
      )}

      {/* Task Breakdown Modal */}
      {showTaskBreakdownModal && analytics?.taskCategories && (
        <TaskBreakdownModal
          isOpen={showTaskBreakdownModal}
          onClose={() => setShowTaskBreakdownModal(false)}
          taskCategories={analytics?.taskCategories || []}
          totalTasks={analytics?.totalTasks || 0}
        />
      )}

      {/* User Details Modal */}
      {showUserDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-secondary-900">User Details: {selectedUser.user.name}</h2>
                <button
                  onClick={() => setShowUserDetailsModal(false)}
                  className="text-secondary-400 hover:text-secondary-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-secondary-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-secondary-600 mb-2">Total Hours</h3>
                  <p className="text-2xl font-bold text-primary-600">{selectedUser.statistics.totalHours.toFixed(1)}h</p>
                  <p className="text-xs text-secondary-500">{selectedUser.period} period</p>
                </div>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-secondary-600 mb-2">Efficiency</h3>
                  <p className="text-2xl font-bold text-primary-600">
                    {selectedUser.statistics.efficiency != null
                      ? `${selectedUser.statistics.efficiency.toFixed(1)}%`
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-secondary-500">Productive time</p>
                </div>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-secondary-600 mb-2">Status</h3>
                  <p className={`text-2xl font-bold ${
                    selectedUser.statistics.utilizationStatus === 'overloaded' ? 'text-orange-600' :
                    selectedUser.statistics.utilizationStatus === 'underutilized' ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {selectedUser.statistics.utilizationStatus.charAt(0).toUpperCase() + selectedUser.statistics.utilizationStatus.slice(1)}
                  </p>
                  <p className="text-xs text-secondary-500">Utilization</p>
                </div>
              </div>

              {/* Tasks Overview */}
              <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-secondary-900 mb-4">Tasks Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary-600">{selectedUser.tasks.total}</p>
                    <p className="text-sm text-secondary-600">Total Tasks</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedUser.tasks.active}</p>
                    <p className="text-sm text-secondary-600">Active</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedUser.tasks.completed}</p>
                    <p className="text-sm text-secondary-600">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{selectedUser.tasks.overdue}</p>
                    <p className="text-sm text-secondary-600">Overdue</p>
                  </div>
                </div>
              </div>

              {/* Projects */}
              {selectedUser.projects.list.length > 0 && (
                <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-secondary-900 mb-4">Projects ({selectedUser.projects.total})</h3>
                  <div className="space-y-3">
                    {selectedUser.projects.list.map((project) => (
                      <div key={project.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-secondary-900">{project.name}</h4>
                          <p className="text-sm text-secondary-600">{project.tasks} tasks</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-primary-600">{project.hours.toFixed(1)}h</p>
                          <p className="text-xs text-secondary-500">
                            {project.activeTasks} active, {project.completedTasks} completed
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Time Entries */}
              {selectedUser.recentTimeEntries.length > 0 && (
                <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-secondary-900 mb-4">Recent Time Entries</h3>
                  <div className="space-y-2">
                    {selectedUser.recentTimeEntries.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                        <div>
                          <p className="font-medium text-secondary-900">{entry.task_name}</p>
                          <p className="text-sm text-secondary-600">{entry.project_name}</p>
                          {entry.description && (
                            <p className="text-xs text-secondary-500">{entry.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-primary-600">{entry.duration.toFixed(1)}h</p>
                          <p className="text-xs text-secondary-500">
                            {toUTCPlus5DateOnly(entry.start_time)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics; 